import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getGlossaryForSet,
  getStandardSets,
  getStandardsForSet,
  type GlossaryTermRecord,
  type StandardRecord,
  type StandardSetSummary,
} from "../../auth/api-client";
import type { MentionKind } from "./mentionTypes";

export interface CatalogPickerItem {
  readonly kind: MentionKind;
  readonly id: string;
  readonly label: string;
  readonly description: string;
}

export function useStandardsCatalog(bearer: string | null, standardSetId: string) {
  const [sets, setSets] = useState<StandardSetSummary[]>([]);
  const [standards, setStandards] = useState<StandardRecord[]>([]);
  const [glossary, setGlossary] = useState<GlossaryTermRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!bearer) return;
    (async () => {
      try {
        const { standardSets } = await getStandardSets(bearer);
        if (!cancelled) setSets(standardSets);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load standard sets");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer]);

  useEffect(() => {
    let cancelled = false;
    if (!bearer || !standardSetId) {
      setStandards([]);
      setGlossary([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [{ standards: stds }, { glossary: gloss }] = await Promise.all([
          getStandardsForSet(bearer, standardSetId),
          getGlossaryForSet(bearer, standardSetId),
        ]);
        if (cancelled) return;
        setStandards(stds);
        setGlossary(gloss);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load standards");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, standardSetId]);

  const allItems = useMemo((): CatalogPickerItem[] => {
    const stdItems: CatalogPickerItem[] = standards.map((s) => ({
      kind: "standard",
      id: s.id,
      label: s.code,
      description: s.description,
    }));
    const glossItems: CatalogPickerItem[] = glossary.map((g) => ({
      kind: "glossary",
      id: g.id,
      label: g.term,
      description: g.definition,
    }));
    return [...stdItems, ...glossItems];
  }, [standards, glossary]);

  const filterItems = useCallback(
    (query: string): CatalogPickerItem[] => {
      const q = query.trim().toLowerCase();
      if (!q) return allItems.slice(0, 80);
      return allItems
        .filter((item) => {
          const label = item.label.toLowerCase();
          const desc = item.description.toLowerCase();
          return label.startsWith(q) || label.includes(q) || desc.includes(q);
        })
        .slice(0, 80);
    },
    [allItems]
  );

  return { sets, standards, glossary, loading, error, filterItems };
}
